import React, { useCallback, useEffect, useMemo, useState } from 'react';
// useCallback is also used inside DocsModal via the module-level import above
import { useAuth } from '../../../context/AuthContext.jsx';
import { apiClient } from '../../../lib/apiClient.js';
import { QUEUE_UPDATED_EVENT } from '../../../lib/events.js';
import FilterSelect from '../../../components/ui/FilterSelect.jsx';
import QueueTable from '../../../components/sponsor/QueueTable.jsx';
import DecisionDialog from '../../../components/sponsor/DecisionDialog.jsx';
import LoadingState from '../../../components/ui/LoadingState.jsx';
import { titleCase } from '../../../lib/format.js';

const STATUS_OPTIONS = [
  { value: 'all',                           label: 'All Statuses' },
  { value: 'pending',                       label: 'Pending' },
  { value: 'approved_awaiting_bank_details', label: 'Awaiting Bank Details' },
  { value: 'approved',                      label: 'Approved' },
  { value: 'denied',                        label: 'Denied' },
];

const AUTO_REFRESH_MS = 15000;

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function OpenFileButton({ docId }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  async function handleOpen() {
    setLoading(true);
    setErr(null);
    try {
      const blobUrl = await apiClient.downloadDocument(docId);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (e) {
      setErr('Could not open file');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={handleOpen}
        disabled={loading}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white hover:bg-accent-dark transition-colors disabled:opacity-60"
      >
        <svg viewBox="0 0 20 20" className="w-3.5 h-3.5 fill-current">
          <path d="M13 8V2H7v6H2l8 8 8-8h-5zM0 18h20v2H0v-2z"/>
        </svg>
        {loading ? 'Opening…' : 'Open File'}
      </button>
      {err && <span className="text-[11px] text-danger">{err}</span>}
    </div>
  );
}

function DocsModal({ entry, onClose, onDocApproved }) {
  const [docs, setDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [approveNote, setApproveNote] = useState('');
  const [error, setError] = useState(null);
  const [approved, setApproved] = useState(false);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await apiClient.getQueueEntryDocs(entry.entry_id);
      setDocs(res.documents || []);
    } catch (err) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  }, [entry.entry_id]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  async function handleApproveDocs() {
    setIsApproving(true);
    setError(null);
    try {
      await apiClient.approveQueueEntryDocs(entry.entry_id, approveNote);
      setApproved(true);
      onDocApproved();
      // Refetch so doc cards update their "Sponsor Approved" badge
      await fetchDocs();
    } catch (err) {
      setError(err.message || 'Failed to approve documents');
    } finally {
      setIsApproving(false);
    }
  }

  const allSponsorApproved = docs.length > 0 && docs.every((d) => d.sponsor_doc_approved);
  const showApproveFooter = !isLoading && docs.length > 0 && !allSponsorApproved && !approved;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
      <div className="bg-bg-surface border border-border-strong rounded-lg w-full max-w-xl shadow-card-hover flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-text">Supporting Documents</h3>
            <p className="text-xs text-text-muted mt-0.5">
              {titleCase(entry.action)} · {entry.participant_id}
            </p>
            <p className="text-[11px] text-text-faint mt-0.5 font-mono">
              Submitted {formatDate(entry.submitted_at)} · Entry {entry.entry_id}
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="text-text-faint hover:text-text text-xl leading-none ml-4" aria-label="Close">
            ×
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {isLoading && (
            <p className="text-sm text-text-muted text-center py-8">Loading documents…</p>
          )}
          {!isLoading && docs.length === 0 && (
            <p className="text-sm text-text-muted text-center py-8">
              No documents uploaded yet for this entry.
            </p>
          )}
          {!isLoading && docs.map((doc) => (
            <div key={doc.doc_id} className="border border-border rounded-lg overflow-hidden">
              {/* Doc header */}
              <div className="px-4 py-3 bg-bg-s2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text truncate">
                    {doc.doc_type_label || doc.doc_type}
                  </p>
                  <p className="text-[11px] text-text-faint font-mono truncate">{doc.filename}</p>
                  {doc.uploaded_at && (
                    <p className="text-[10px] text-text-faint mt-0.5">
                      Uploaded {formatDate(doc.uploaded_at)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {doc.verified ? (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-success/15 text-success border border-success/30 font-medium">
                      LLM Verified
                    </span>
                  ) : (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-warning/15 text-warning border border-warning/30 font-medium">
                      Needs Review
                    </span>
                  )}
                  {doc.sponsor_doc_approved && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/30 font-medium">
                      Admin ✓
                    </span>
                  )}
                </div>
              </div>

              {/* Download / view */}
              {doc.download_url ? (
                <div className="px-4 py-3 border-t border-border flex items-center gap-3">
                  <OpenFileButton docId={doc.doc_id} />
                </div>
              ) : doc.content_preview ? (
                <div className="px-4 py-3 border-t border-border">
                  <p className="text-[10px] font-mono text-text-faint uppercase tracking-wide mb-1">
                    Document Preview
                  </p>
                  <pre className="text-[11px] text-text-muted bg-bg-s2 rounded p-2.5 whitespace-pre-wrap font-mono leading-relaxed max-h-48 overflow-y-auto border border-border/50">
                    {doc.content_preview}
                  </pre>
                </div>
              ) : null}

              {/* Verification note */}
              {doc.verification_note && (
                <div className="px-4 py-2 border-t border-border/50 bg-bg">
                  <p className="text-xs text-text-muted italic">{doc.verification_note}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border space-y-3">
          {error && <p className="text-xs text-danger">{error}</p>}
          {(approved || allSponsorApproved) && (
            <p className="text-xs text-success font-medium">
              Documents approved — you can now approve the request.
            </p>
          )}

          {showApproveFooter && (
            <>
              <label className="block">
                <span className="label-mono">Approval Note</span>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. Documentation reviewed and verified"
                  value={approveNote}
                  onChange={(e) => setApproveNote(e.target.value)}
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleApproveDocs}
                  disabled={isApproving}
                  className="px-4 py-2 rounded-md text-[13px] font-medium text-white bg-success hover:bg-success/80 transition-colors disabled:opacity-50"
                >
                  {isApproving ? 'Approving…' : 'Approve Documents'}
                </button>
                <button type="button" onClick={onClose} className="btn-secondary">
                  Close
                </button>
              </div>
            </>
          )}

          {!showApproveFooter && (
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className="btn-secondary">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Details Modal ──────────────────────────────────────────────────────────────

const EXPENSE_LABELS = {
  medical_expenses:    'Medical Expenses',
  tuition:             'Post-Secondary Tuition',
  home_purchase:       'Primary Residence Purchase',
  eviction_prevention: 'Eviction / Foreclosure Prevention',
  funeral_costs:       'Funeral / Burial Costs',
  casualty_loss:       'Casualty Loss',
  fema_disaster:       'FEMA-Declared Disaster',
};

function SectionLabel({ children }) {
  return (
    <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-2 mt-5 first:mt-0">
      {children}
    </p>
  );
}

function BeneficiaryList({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <div className="rounded-md border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-bg-s2 text-left text-[11px] font-mono text-text-muted">
              <th className="px-3 py-2 pr-4">Name</th>
              <th className="px-3 py-2 pr-4">Relationship</th>
              <th className="px-3 py-2 text-right">Allocation</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="px-3 py-2 pr-4 font-medium text-text">{b.name || '—'}</td>
                <td className="px-3 py-2 pr-4 text-text-muted capitalize">{b.relationship || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium text-text">{b.allocation_pct != null ? `${b.allocation_pct}%` : '100%'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailRow({ label, value, highlight }) {
  if (value == null || value === '' || value === '—') return null;
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border/40 last:border-0">
      <span className="text-[12px] text-text-faint shrink-0">{label}</span>
      <span className={`text-[13px] font-medium text-right ${highlight ? 'text-accent' : 'text-text'}`}>{value}</span>
    </div>
  );
}

function fmt(amount) {
  if (amount == null) return null;
  return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

function StatCell({ label, value, accent }) {
  if (value == null) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-mono uppercase tracking-widest text-text-faint">{label}</span>
      <span className={`text-[13px] font-semibold tabular-nums ${accent ? 'text-accent' : 'text-text'}`}>{value}</span>
    </div>
  );
}

function ParticipantContext({ ctx, action }) {
  if (!ctx || Object.keys(ctx).length === 0) return null;

  const empLabel = ctx.employment_status
    ? ctx.employment_status.charAt(0).toUpperCase() + ctx.employment_status.slice(1)
    : null;

  // IRC §72(p) max loan: lesser of $50k or 50% of vested
  const loanHeadroom = (action === 'loan_initiation' && ctx.vested_balance)
    ? Math.min(50000, ctx.vested_balance * 0.5)
    : null;

  return (
    <div className="rounded-md bg-bg-s2 border border-border px-4 py-3 mb-5">
      <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-3">Participant Overview</p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        <StatCell label="Employment Status" value={empLabel} />
        <StatCell label="Vested Balance" value={fmt(ctx.vested_balance)} />
        <StatCell label="Total Balance" value={fmt(ctx.total_balance)} />
        <StatCell label="Current Deferral" value={ctx.deferral_pct != null ? `${(ctx.deferral_pct * 100).toFixed(1)}%` : null} />
        {loanHeadroom != null && (
          <StatCell label="Max Loan (§72p)" value={fmt(loanHeadroom)} accent />
        )}
      </div>
    </div>
  );
}

function DetailsModal({ entry, onClose }) {
  const p = entry.payload || {};
  const ctx = entry.participant_ctx || {};
  const action = entry.action;

  function renderPayload() {
    if (action === 'beneficiary_update') {
      const primary = p.primary_beneficiaries || (p.beneficiary_name ? [{ name: p.beneficiary_name, relationship: p.relationship, allocation_pct: p.allocation_pct ?? 100 }] : []);
      const contingent = p.contingent_beneficiaries || [];
      return (
        <div className="space-y-5">
          <BeneficiaryList label="Primary Beneficiaries" items={primary} />
          {contingent.length > 0 && <BeneficiaryList label="Contingent Beneficiaries" items={contingent} />}
          {primary.length === 0 && contingent.length === 0 && (
            <p className="text-sm text-text-muted italic">No beneficiary details in payload.</p>
          )}
        </div>
      );
    }

    if (action === 'qdro') {
      // Calculate estimated transfer amount if we have vested balance and transfer_pct
      const transferAmt = (p.transfer_pct != null && ctx.vested_balance)
        ? ctx.vested_balance * (p.transfer_pct / 100)
        : null;
      return (
        <div>
          <SectionLabel>QDRO Transfer Details</SectionLabel>
          <DetailRow label="Alternate Payee" value={p.alternate_payee_name} />
          <DetailRow label="Transfer Percentage" value={p.transfer_pct != null ? `${p.transfer_pct}%` : null} />
          {transferAmt != null && (
            <DetailRow label="Estimated Transfer Amount" value={fmt(transferAmt)} highlight />
          )}
          <DetailRow label="Benefit Amount / Pct" value={p.benefit_amount_or_pct} />
          <p className="text-[11px] text-text-faint mt-3">
            ERISA § 206(d) — QDRO determination required within 18 months. Must verify court order.
          </p>
        </div>
      );
    }

    if (action === 'hardship_distribution') {
      const pct = (p.amount != null && ctx.vested_balance)
        ? ((p.amount / ctx.vested_balance) * 100).toFixed(1)
        : null;
      return (
        <div>
          <SectionLabel>Hardship Distribution Details</SectionLabel>
          <DetailRow label="Requested Amount" value={fmt(p.amount)} highlight />
          {pct != null && (
            <DetailRow label="As % of Vested Balance" value={`${pct}%`} />
          )}
          <DetailRow label="Qualifying Expense" value={EXPENSE_LABELS[p.qualifying_expense_type] || titleCase(p.qualifying_expense_type || '')} />
          <DetailRow label="Tax Acknowledged" value={p.taxable_event_acknowledged ? 'Yes' : null} />
          <p className="text-[11px] text-text-faint mt-3">
            IRC § 401(k)(2)(B)(i)(IV) — hardship must meet the immediate and heavy financial need standard.
            Supporting documentation required before approval.
          </p>
        </div>
      );
    }

    if (action === 'in_service_distribution') {
      const pct = (p.amount != null && ctx.vested_balance)
        ? ((p.amount / ctx.vested_balance) * 100).toFixed(1)
        : null;
      return (
        <div>
          <SectionLabel>In-Service Distribution Details</SectionLabel>
          <DetailRow label="Requested Amount" value={fmt(p.amount)} highlight />
          {pct != null && (
            <DetailRow label="As % of Vested Balance" value={`${pct}%`} />
          )}
          <DetailRow label="Tax Acknowledged" value={p.taxable_event_acknowledged ? 'Yes' : null} />
          <p className="text-[11px] text-text-faint mt-3">
            IRC § 401(a)(36) — in-service distributions permitted at age 59½ or older.
          </p>
        </div>
      );
    }

    if (action === 'separation_distribution') {
      const pct = (p.amount != null && ctx.vested_balance)
        ? ((p.amount / ctx.vested_balance) * 100).toFixed(1)
        : null;
      return (
        <div>
          <SectionLabel>Separation Distribution Details</SectionLabel>
          <DetailRow label="Requested Amount" value={fmt(p.amount)} highlight />
          {pct != null && (
            <DetailRow label="As % of Vested Balance" value={`${pct}%`} />
          )}
          <DetailRow label="Tax Acknowledged" value={p.taxable_event_acknowledged ? 'Yes' : null} />
          <DetailRow label="Rollover Notice (§402(f))" value={p.rollover_402f_notice_confirmed ? 'Confirmed' : null} />
          <p className="text-[11px] text-text-faint mt-3">
            IRC § 401(a)(31) — rollover rights must be communicated before distribution. Verify separation from service.
          </p>
        </div>
      );
    }

    if (action === 'rmd') {
      return (
        <div>
          <SectionLabel>Required Minimum Distribution Details</SectionLabel>
          <DetailRow label="Distribution Amount" value={fmt(p.amount)} highlight />
          <DetailRow label="RMD Notice Issued" value={p.rmd_notice_issued ? 'Yes' : null} />
          <DetailRow label="Tax Acknowledged" value={p.taxable_event_acknowledged ? 'Yes' : null} />
          <p className="text-[11px] text-text-faint mt-3">
            IRC § 401(a)(9) / SECURE 2.0 — RMDs begin at age 73. Failure to distribute results in
            a 25% excise tax under IRC § 4974.
          </p>
        </div>
      );
    }

    if (action === 'loan_initiation') {
      return (
        <div>
          <SectionLabel>Loan Request Details</SectionLabel>
          <DetailRow label="Loan Amount" value={fmt(p.amount)} highlight />
          <DetailRow label="Repayment Period" value={p.repayment_years != null ? `${p.repayment_years} year(s)` : null} />
          <DetailRow label="Purpose" value={p.purpose ? titleCase(p.purpose.replace(/_/g, ' ')) : null} />
          <p className="text-[11px] text-text-faint mt-3">
            IRC § 72(p) — maximum loan is the lesser of $50,000 or 50% of vested balance.
            Loans for primary residence may extend to 15 years.
          </p>
        </div>
      );
    }

    // Fallback: show raw payload keys
    return (
      <div>
        <SectionLabel>Request Payload</SectionLabel>
        {Object.entries(p).map(([k, v]) => (
          <DetailRow key={k} label={k.replace(/_/g, ' ')} value={typeof v === 'object' ? JSON.stringify(v) : String(v)} />
        ))}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
      <div className="bg-bg-surface border border-border-strong rounded-lg w-full max-w-lg shadow-card-hover flex flex-col max-h-[85vh]">
        <div className="px-5 py-4 border-b border-border flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-text">Request Details</h3>
            <p className="text-xs text-text-muted mt-0.5">{titleCase(entry.action)} · {entry.participant_name || entry.participant_id}</p>
            <p className="text-[11px] text-text-faint mt-0.5 font-mono">Entry {entry.entry_id} · Submitted {new Date(entry.submitted_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</p>
          </div>
          <button type="button" onClick={onClose} className="text-text-faint hover:text-text text-xl leading-none ml-4" aria-label="Close">×</button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4">
          <ParticipantContext ctx={entry.participant_ctx} action={action} />
          {renderPayload()}
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end">
          <button type="button" onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}

export default function Queue() {
  const { principal } = useAuth();
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [dialog, setDialog] = useState(null); // { entry, mode }
  const [docsEntry, setDocsEntry] = useState(null); // entry whose docs are open
  const [detailsEntry, setDetailsEntry] = useState(null); // entry whose details are open
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    try {
      const res = await apiClient.getQueue();
      setEntries(res.entries || []);
    } catch {
      // leave existing state on error
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    function onQueueUpdated() {
      load({ silent: true });
    }
    window.addEventListener(QUEUE_UPDATED_EVENT, onQueueUpdated);
    const interval = setInterval(() => load({ silent: true }), AUTO_REFRESH_MS);
    return () => {
      window.removeEventListener(QUEUE_UPDATED_EVENT, onQueueUpdated);
      clearInterval(interval);
    };
  }, [load]);

  const planScoped = useMemo(
    () => entries.filter((e) => !principal?.planId || e.plan_id === principal.planId),
    [entries, principal?.planId],
  );

  const actionOptions = useMemo(() => {
    const unique = Array.from(new Set(planScoped.map((e) => e.action)));
    return [{ value: 'all', label: 'All Actions' }, ...unique.map((a) => ({ value: a, label: titleCase(a) }))];
  }, [planScoped]);

  const filtered = useMemo(
    () =>
      planScoped.filter(
        (e) =>
          (statusFilter === 'all' || e.status === statusFilter) &&
          (actionFilter === 'all' || e.action === actionFilter),
      ),
    [planScoped, statusFilter, actionFilter],
  );

  async function handleDecision(note) {
    if (!dialog) return;
    setIsSubmitting(true);
    setError(null);
    try {
      if (dialog.mode === 'approve') {
        await apiClient.approveQueueEntry(dialog.entry.entry_id, note);
      } else {
        await apiClient.denyQueueEntry(dialog.entry.entry_id, note);
      }
      setDialog(null);
      await load({ silent: true });
    } catch (err) {
      setError(err.message || 'Failed to submit decision');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return <LoadingState label="Loading review queue…" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-text-muted mb-4 max-w-2xl">
          Hardship distributions, QDROs, and beneficiary updates flagged for human review.
        </p>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
          <FilterSelect label="Action" value={actionFilter} onChange={setActionFilter} options={actionOptions} />
          <span className="text-xs text-text-faint sm:ml-auto">
            {filtered.length} of {planScoped.length} entries
          </span>
        </div>

        {error && <p className="text-xs text-danger mb-3">{error}</p>}

        <div className="card p-4 sm:p-6">
          <QueueTable
            entries={filtered}
            onApprove={(entry) => setDialog({ entry, mode: 'approve' })}
            onDeny={(entry) => setDialog({ entry, mode: 'deny' })}
            onViewDocs={(entry) => setDocsEntry(entry)}
            onViewDetails={(entry) => setDetailsEntry(entry)}
          />
        </div>
      </div>

      {dialog && (
        <DecisionDialog
          entry={dialog.entry}
          mode={dialog.mode}
          isSubmitting={isSubmitting}
          onSubmit={handleDecision}
          onCancel={() => setDialog(null)}
        />
      )}

      {docsEntry && (
        <DocsModal
          entry={docsEntry}
          onClose={() => setDocsEntry(null)}
          onDocApproved={() => {
            load({ silent: true });
          }}
        />
      )}

      {detailsEntry && (
        <DetailsModal
          entry={detailsEntry}
          onClose={() => setDetailsEntry(null)}
        />
      )}
    </div>
  );
}
